// Subtitle parsers + emitters for formats not covered by subsrt-ts.
// Internal representation: { start: ms, end: ms, text: string } cues.
// FEATURE_EXPANSION_RESEARCH.md §5.1 — S4.

export interface Cue {
    start: number; // ms from media start
    end: number;
    text: string;
}

function fmtTime(ms: number, sep = ',', includeHr = true): string {
    const totalSec = ms / 1000;
    const hr = Math.floor(totalSec / 3600);
    const min = Math.floor((totalSec % 3600) / 60);
    const sec = Math.floor(totalSec % 60);
    const millis = Math.round(ms % 1000);
    const pad = (n: number, l = 2) => n.toString().padStart(l, '0');
    return includeHr
        ? `${pad(hr)}:${pad(min)}:${pad(sec)}${sep}${pad(millis, 3)}`
        : `${pad(min)}:${pad(sec)}${sep}${pad(millis, 3)}`;
}

function parseTime(s: string): number {
    // Accepts hh:mm:ss,ms or hh:mm:ss.ms or mm:ss.ms
    const m = s.trim().match(/^(?:(\d+):)?(\d+):(\d+)[,.](\d+)$/);
    if (!m) return 0;
    const hr = +(m[1] || 0);
    const min = +m[2];
    const sec = +m[3];
    const ms = +m[4].padEnd(3, '0').substring(0, 3);
    return hr * 3600000 + min * 60000 + sec * 1000 + ms;
}

// ─── LRC ──────────────────────────────────────────────────────────────────
export function parseLrc(text: string): Cue[] {
    const cues: Cue[] = [];
    const lines = text.split(/\r?\n/);
    const tsRx = /\[(\d{1,2}):(\d{2})(?:\.(\d{1,3}))?\]/g;
    const starts: { time: number; text: string }[] = [];
    for (const line of lines) {
        if (line.startsWith('[ti:') || line.startsWith('[ar:') || line.startsWith('[al:') || line.startsWith('[by:') || line.startsWith('[offset:')) continue;
        const matches = [...line.matchAll(tsRx)];
        if (matches.length === 0) continue;
        const text = line.replace(tsRx, '').trim();
        for (const m of matches) {
            const t = (+m[1]) * 60000 + (+m[2]) * 1000 + (+(m[3] || '0').padEnd(3, '0').substring(0, 3));
            starts.push({ time: t, text });
        }
    }
    starts.sort((a, b) => a.time - b.time);
    for (let i = 0; i < starts.length; i++) {
        const next = starts[i + 1];
        cues.push({ start: starts[i].time, end: next ? next.time : starts[i].time + 4000, text: starts[i].text });
    }
    return cues;
}

export function emitLrc(cues: Cue[]): string {
    return cues.map(c => {
        const totalSec = c.start / 1000;
        const min = Math.floor(totalSec / 60);
        const sec = Math.floor(totalSec % 60);
        const ms = Math.floor((c.start % 1000) / 10);
        return `[${min.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}.${ms.toString().padStart(2, '0')}]${c.text}`;
    }).join('\n') + '\n';
}

// ─── SMI (SAMI) ──────────────────────────────────────────────────────────
export function parseSmi(text: string): Cue[] {
    const cues: Cue[] = [];
    const syncs = [...text.matchAll(/<sync[^>]*start=["']?(\d+)["']?[^>]*>([\s\S]*?)(?=<sync|<\/body>)/gi)];
    for (let i = 0; i < syncs.length; i++) {
        const start = +syncs[i][1];
        const body = syncs[i][2].replace(/<[^>]+>/g, '').trim();
        const next = syncs[i + 1];
        const end = next ? +next[1] : start + 4000;
        if (body && !/^&nbsp;$/i.test(body)) {
            cues.push({ start, end, text: body });
        }
    }
    return cues;
}

export function emitSmi(cues: Cue[]): string {
    const body = cues.map(c => `<SYNC Start=${c.start}><P Class=ENCC>${c.text.replace(/\n/g, '<BR>')}</P>\n<SYNC Start=${c.end}><P Class=ENCC>&nbsp;</P>`).join('\n');
    return `<SAMI>\n<HEAD>\n<TITLE>Subtitles</TITLE>\n<STYLE TYPE="text/css">\n<!--\nP { font-family: Arial; font-size: 16pt; color: white; background-color: black; }\n.ENCC { Name: English; lang: en-US; }\n-->\n</STYLE>\n</HEAD>\n<BODY>\n${body}\n</BODY>\n</SAMI>`;
}

// ─── TTML / DFXP ─────────────────────────────────────────────────────────
function fmtTtmlTime(ms: number): string {
    return fmtTime(ms, '.', true);
}

export function parseTtml(text: string): Cue[] {
    const cues: Cue[] = [];
    const ps = [...text.matchAll(/<p\b[^>]*\bbegin=["']([^"']+)["'][^>]*\bend=["']([^"']+)["'][^>]*>([\s\S]*?)<\/p>/gi)];
    for (const m of ps) {
        const body = m[3].replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]+>/g, '').trim();
        cues.push({ start: parseTime(m[1].replace('.', ',')), end: parseTime(m[2].replace('.', ',')), text: body });
    }
    return cues;
}

export function emitTtml(cues: Cue[], isDfxp = false): string {
    const ns = isDfxp ? 'xmlns="http://www.w3.org/ns/ttml" xmlns:tts="http://www.w3.org/ns/ttml#styling"' : 'xmlns="http://www.w3.org/ns/ttml"';
    const ps = cues.map(c =>
        `      <p begin="${fmtTtmlTime(c.start)}" end="${fmtTtmlTime(c.end)}">${c.text.replace(/\n/g, '<br/>')}</p>`,
    ).join('\n');
    return `<?xml version="1.0" encoding="UTF-8"?>\n<tt ${ns}>\n  <body>\n    <div>\n${ps}\n    </div>\n  </body>\n</tt>`;
}

// ─── EBU STL (text representation, not binary EBU 3264-E) ────────────────
export function parseStl(text: string): Cue[] {
    // Plain-text STL variant: "hh:mm:ss:ff , hh:mm:ss:ff , text"
    const cues: Cue[] = [];
    for (const line of text.split(/\r?\n/)) {
        const m = line.match(/^(\d+:\d+:\d+):(\d+)\s*[,]\s*(\d+:\d+:\d+):(\d+)\s*,\s*(.+)$/);
        if (!m) continue;
        const start = parseTime(m[1] + ',' + (+m[2] / 25 * 1000).toFixed(0));
        const end = parseTime(m[3] + ',' + (+m[4] / 25 * 1000).toFixed(0));
        cues.push({ start, end, text: m[5].replace(/\|/g, '\n') });
    }
    return cues;
}

export function emitStl(cues: Cue[]): string {
    return cues.map(c => {
        const fps = 25;
        const ft = (ms: number) => {
            const totalSec = Math.floor(ms / 1000);
            const frame = Math.round((ms % 1000) / 1000 * fps);
            const hr = Math.floor(totalSec / 3600);
            const min = Math.floor((totalSec % 3600) / 60);
            const sec = totalSec % 60;
            const p = (n: number) => n.toString().padStart(2, '0');
            return `${p(hr)}:${p(min)}:${p(sec)}:${p(frame)}`;
        };
        return `${ft(c.start)} , ${ft(c.end)} , ${c.text.replace(/\n/g, '|')}`;
    }).join('\n') + '\n';
}

// ─── TXT (plain text, no timestamps) ─────────────────────────────────────
export function parseTxt(text: string): Cue[] {
    // Each non-empty line becomes a 4-second cue. A blank line between two
    // lines (paragraph break) inserts an extra 4s gap — earlier behaviour
    // treated paragraphs as cues which silently dropped multi-line input.
    const cues: Cue[] = [];
    const lines = text.split(/\r?\n/);
    let cueIdx = 0;
    for (const raw of lines) {
        const t = raw.trim();
        if (!t) { cueIdx++; continue; }
        cues.push({ start: cueIdx * 4000, end: (cueIdx + 1) * 4000, text: t });
        cueIdx++;
    }
    return cues;
}

export function emitTxt(cues: Cue[]): string {
    return cues.map(c => c.text).join('\n\n') + '\n';
}

// ─── CSV ─────────────────────────────────────────────────────────────────
function csvEscape(s: string): string {
    return /[,"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function csvParseRow(line: string): string[] {
    const out: string[] = [];
    let cur = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
        const c = line[i];
        if (inQuotes) {
            if (c === '"') {
                if (line[i + 1] === '"') { cur += '"'; i++; }
                else inQuotes = false;
            } else cur += c;
        } else {
            if (c === ',') { out.push(cur); cur = ''; }
            else if (c === '"' && cur === '') inQuotes = true;
            else cur += c;
        }
    }
    out.push(cur);
    return out;
}

export function parseCsv(text: string): Cue[] {
    const cues: Cue[] = [];
    const lines = text.split(/\r?\n/);
    const start = lines[0]?.toLowerCase().includes('start') ? 1 : 0;
    for (let i = start; i < lines.length; i++) {
        const row = csvParseRow(lines[i]);
        if (row.length < 3) continue;
        cues.push({
            start: parseTime(row[0]),
            end: parseTime(row[1]),
            text: row.slice(2).join(', ').trim(),
        });
    }
    return cues;
}

export function emitCsv(cues: Cue[]): string {
    const header = 'Start,End,Text\n';
    return header + cues.map(c =>
        `${csvEscape(fmtTime(c.start))},${csvEscape(fmtTime(c.end))},${csvEscape(c.text)}`,
    ).join('\n') + '\n';
}

// ─── ASS / SSA (custom, bypasses subsrt-ts text-extraction bug) ──────────
// subsrt-ts's Dialogue parser uses `value.split(",", N).join(",").length + 2`
// which is off by one — it drops the first character of every Text field
// (e.g. "Hello world" → "ello world"). We re-parse the format ourselves
// and only use the library for time/meta.

function assToMilliseconds(s: string): number {
    const m = /^\s*(\d+):(\d{1,2}):(\d{1,2})(?:[.,](\d{1,3}))?\s*$/.exec(s);
    if (!m) return 0;
    const hh = +m[1], mm = +m[2], ss = +m[3];
    const cs = m[4] ? +m[4].padEnd(2, '0').slice(0, 2) : 0;
    return hh * 3600000 + mm * 60000 + ss * 1000 + cs * 10;
}

function splitAssDialogueValue(value: string, columnCount: number): string[] {
    // Standard split-by-comma stops at the last column, preserving any
    // commas embedded in the Text field.
    const out: string[] = [];
    let start = 0;
    for (let i = 0; i < columnCount - 1; i++) {
        const idx = value.indexOf(',', start);
        if (idx < 0) {
            out.push(value.substring(start).trim());
            return out;
        }
        out.push(value.substring(start, idx).trim());
        start = idx + 1;
    }
    out.push(value.substring(start));
    return out;
}

export function parseAss(text: string): Cue[] {
    const cues: Cue[] = [];
    let columns: string[] | null = null;
    let section: string | null = null;

    for (const raw of text.split(/\r?\n/)) {
        const line = raw.trimStart();
        if (!line) continue;
        const sectionMatch = line.match(/^\[([^\]]+)\]\s*$/);
        if (sectionMatch) { section = sectionMatch[1].trim(); continue; }
        if (line.startsWith(';')) continue;
        if (section !== 'Events') continue;

        const kv = line.match(/^([A-Za-z]+):\s*(.*)$/);
        if (!kv) continue;
        const name = kv[1].trim();
        const value = kv[2];
        if (name === 'Format') {
            columns = value.split(/\s*,\s*/);
            continue;
        }
        if (name === 'Dialogue' && columns) {
            const values = splitAssDialogueValue(value, columns.length);
            const data: Record<string, string> = {};
            for (let i = 0; i < columns.length; i++) data[columns[i]] = values[i] || '';
            const txt = (data.Text || '').replace(/\\N/g, '\n').replace(/\{[^}]+\}/g, '');
            cues.push({
                start: assToMilliseconds(data.Start || '0:00:00.00'),
                end: assToMilliseconds(data.End || '0:00:00.00'),
                text: txt,
            });
        }
    }
    return cues;
}

// SSA V4 (not V4+) build in subsrt-ts emits PrimaryColour/etc. as raw
// decimals instead of ASS's `&HAABBGGRR` hex. Patch the Style line on
// the way out so libass/VLC/Aegisub can render it.
export function fixSsaColours(ssa: string): string {
    return ssa.replace(
        /^Style:\s*DefaultVCD,\s*Arial,28,11861244,11861244,11861244,-2147483640,(.*)$/m,
        'Style: DefaultVCD, Arial,28,&H00B4FCFC,&H00B4FCFC,&H00B4FCFC,&H80000008,$1',
    );
}

// ─── Generic dispatcher ───────────────────────────────────────────────────
export type SubtitleFormat = 'srt' | 'vtt' | 'ass' | 'ssa' | 'sbv' | 'sub' | 'lrc' | 'smi' | 'ttml' | 'stl' | 'dfxp' | 'txt' | 'csv';

// subsrt-ts handles these natively — we still rely on it for srt/vtt/sbv/sub.
// ASS/SSA go through our custom parser (above) because subsrt's text
// extraction is broken.
export const SUBSRT_FORMATS: SubtitleFormat[] = ['srt', 'vtt', 'sbv', 'sub'];

export function isSubsrtFormat(f: SubtitleFormat): boolean {
    return SUBSRT_FORMATS.includes(f);
}

// Cue conversion to subsrt-ts internal format (ms-based) and back.
export function cuesToSubsrt(cues: Cue[]): any[] {
    return cues.map(c => ({ start: c.start, end: c.end, text: c.text }));
}

export function subsrtToCues(captions: any[]): Cue[] {
    return captions.filter(c => c.type !== 'meta' && c.type !== 'header' && typeof c.start === 'number').map(c => ({
        start: c.start, end: c.end, text: (c.text || c.content || '').replace(/<[^>]+>/g, '').trim(),
    }));
}

export function parseCustom(format: SubtitleFormat, text: string): Cue[] {
    switch (format) {
        case 'ass':
        case 'ssa': return parseAss(text);
        case 'lrc': return parseLrc(text);
        case 'smi': return parseSmi(text);
        case 'ttml':
        case 'dfxp': return parseTtml(text);
        case 'stl': return parseStl(text);
        case 'txt': return parseTxt(text);
        case 'csv': return parseCsv(text);
        default: return [];
    }
}

export function emitCustom(format: SubtitleFormat, cues: Cue[]): string {
    switch (format) {
        case 'lrc': return emitLrc(cues);
        case 'smi': return emitSmi(cues);
        case 'ttml': return emitTtml(cues, false);
        case 'dfxp': return emitTtml(cues, true);
        case 'stl': return emitStl(cues);
        case 'txt': return emitTxt(cues);
        case 'csv': return emitCsv(cues);
        default: return '';
    }
}
